// Submit queue — the claims a biller has said are clean and ready to send, for a
// practice whose data source does not submit claims itself.
//
// Registry page (features/registry.js, `feature: 'submit_queue'`). App.js renders
// <FeatureOff feature="submit_queue"/> when the feature is off, so this page never
// checks the gate to decide whether to exist.
//
// The queue is worked by a person or by the Cowork skill; both write the same
// rows. This page shows what is waiting, how old it is, whether the skill is
// alive, and lets a biller take a claim back off.
//
// PHI: the list shows claim id and date of service, never a patient name. The
// name lives in the ledger's claim detail, one click away.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { formatCurrency } from "../../utils/formatCurrency";
import {
  fetchDrainStatus,
  fetchQueue,
  fetchRunbook,
  fetchSignInState,
  canRemove,
  isActionable,
  leaseIsLive,
  parseServerTime,
} from "./api";
import { ageLabel, ageTone, lastRunState, relativeTime } from "./ageing";
import DrainHeader from "./DrainHeader";
import RemoveModal from "./RemoveModal";
import RunbookEditor from "./RunbookEditor";
import RunsList from "./RunsList";
import SkillPage from "./SkillPage";
import SignInBanner from "./SignInBanner";
import NotSentModal from "./NotSentModal";
import ReconciliationPanel from "./ReconciliationPanel";
import SubmittedModal from "./SubmittedModal";
import "./Queue.css";

const STATUS_LABEL = {
  queued: "Queued",
  leased: "Leased",
  held: "Held — check the portal",
  submitted: "Submitted",
  removed: "Removed",
};

const LIVE = ["queued", "leased", "held"];

// Held rows first — submit was pressed and nobody knows whether the claim went, so
// a person has to look before anything else happens to it. Then the rest of the
// live rows, then the last 7 days of submitted/removed history; each group oldest
// first, because the claim closest to its filing limit goes next.
function group(status) {
  if (status === "held") return 0;
  return LIVE.includes(status) ? 1 : 2;
}

function byLiveThenOldest(a, b) {
  const la = group(a.status);
  const lb = group(b.status);
  if (la !== lb) return la - lb;
  const ta = parseServerTime(a.enqueuedAt)?.getTime() ?? 0;
  const tb = parseServerTime(b.enqueuedAt)?.getTime() ?? 0;
  if (ta !== tb) return ta - tb;
  return (a.id || 0) - (b.id || 0);
}

export function LastRunBanner({ run, now }) {
  const state = lastRunState(run, now || new Date());
  return (
    <div className={`sq-run-banner ${state.tone}`} role="status">
      <span>{state.text}</span>
      {state.detail && <span className="sq-run-detail">{state.detail}</span>}
    </div>
  );
}

// No router: the page's sub-views are tabs in local state, like the worklist's
// status filter.
const TABS = [
  { key: "queue", label: "Queue" },
  { key: "runbook", label: "Runbook" },
  { key: "runs", label: "Runs" },
  { key: "skill", label: "Run the skill" },
];

// While any row holds a lease, re-render on this cadence so "I submitted" unlocks
// when the lease expires without waiting for a refresh.
const LEASE_TICK_MS = 30000;

export default function QueuePage({
  selectedPractice,
  readOnly = false,
  isAdmin = false,
  isSuperAdmin = false,
  onPageChange,
}) {
  const practiceId = selectedPractice?.id;
  const [tab, setTab] = useState("queue");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [submitting, setSubmitting] = useState(null);
  const [notSent, setNotSent] = useState(null);
  // null while unknown (loading, or the fetch failed); the modal then refuses the
  // portal channel rather than let the server guess the far side.
  const [portals, setPortals] = useState(null);
  const [runbookVisited, setRunbookVisited] = useState(false);
  // The drain header: depth, the last finished run, and when the queue was last
  // emptied. Its own read, because it is counts and timestamps only — no claim ids —
  // so a failure here must not take the queue list down with it.
  const [drain, setDrain] = useState(null);
  const [drainError, setDrainError] = useState(null);
  // Brief 15: whether the last run stopped at the portal's sign-in. Read beside the
  // list like the drain strip, for the same reason: its failure must not take the
  // queue down with it.
  const [signIn, setSignIn] = useState(null);
  const [signInError, setSignInError] = useState(null);
  const [now, setNow] = useState(() => new Date());

  // Every request takes a ticket; only the newest may write, so switching practice
  // mid-flight never paints one practice's queue under another's name.
  const ticket = useRef(0);

  const load = useCallback(async () => {
    if (!practiceId) return;
    const mine = (ticket.current += 1);
    setLoading(true);
    setError(null);
    try {
      const body = await fetchQueue(practiceId);
      if (mine !== ticket.current) return;
      setData(body);
    } catch (err) {
      if (mine !== ticket.current) return;
      setError(err.message);
      // Never keep the previous practice's rows under an error for this one.
      setData(null);
    } finally {
      if (mine === ticket.current) setLoading(false);
    }
    try {
      const status = await fetchDrainStatus(practiceId);
      if (mine !== ticket.current) return;
      setDrain(status);
      setDrainError(null);
    } catch (err) {
      if (mine !== ticket.current) return;
      setDrain(null);
      setDrainError(
        `${err.message} The queue below is still current; only this strip is missing.`,
      );
    }
    try {
      const state = await fetchSignInState(practiceId);
      if (mine !== ticket.current) return;
      setSignIn(state);
      setSignInError(null);
    } catch (err) {
      if (mine !== ticket.current) return;
      setSignIn(null);
      setSignInError(
        `${err.message} The queue below is still current; only the sign-in state is missing.`,
      );
    }
  }, [practiceId]);

  useEffect(() => {
    setData(null);
    setDrain(null);
    setDrainError(null);
    setSignIn(null);
    setSignInError(null);
    setMessage(null);
    setRemoving(null);
    setSubmitting(null);
    setNotSent(null);
    load();
  }, [load]);

  // The practice's portal keys, for the "I submitted" modal. On failure `portals`
  // stays null and the modal says so and refuses the portal channel: without the
  // key, a Claim.MD portal submission would be recorded against "manual" and skip
  // the Claim.MD id check.
  useEffect(() => {
    if (!practiceId) return undefined;
    let cancelled = false;
    setPortals(null);
    fetchRunbook(practiceId)
      .then((body) => {
        if (!cancelled) setPortals(body.portals);
      })
      .catch(() => {
        if (!cancelled) setPortals(null);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  const items = useMemo(
    () => (data ? [...data.items].sort(byLiveThenOldest) : []),
    [data],
  );

  const hasLeased = items.some((item) => item.status === "leased");
  useEffect(() => {
    if (!hasLeased) return undefined;
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), LEASE_TICK_MS);
    return () => clearInterval(id);
  }, [hasLeased]);

  const onSubmitted = (item, body) => {
    setSubmitting(null);
    const ref = body?.item?.confirmation_ref;
    setMessage({
      type: "success",
      text: `Claim #${item.claimId} was recorded as sent${ref ? ` (ref ${ref})` : ""}.`,
    });
    load();
  };

  const onNotSent = (item) => {
    setNotSent(null);
    setMessage({
      type: "success",
      text: `Claim #${item.claimId} was returned to the queue as not sent.`,
    });
    load();
  };

  const onRemoved = (item) => {
    setRemoving(null);
    setMessage({
      type: "success",
      text: `Claim #${item.claimId} was removed from the queue.`,
    });
    load();
  };

  if (!practiceId) {
    return (
      <div className="sq-page">
        <p className="sq-empty">Select a practice to see its submit queue.</p>
      </div>
    );
  }

  const summary = data?.summary;

  return (
    <div className="sq-page">
      <div className="sq-header">
        <div>
          <h1>Submit queue</h1>
          <p className="sq-sub">
            Clean claims waiting to be sent for {selectedPractice.name}, oldest
            first, then the last 7 days of submitted and removed claims. A claim
            leaves the queue when someone marks it submitted with the portal’s
            reference, or removes it with a reason.
          </p>
        </div>
        {tab === "queue" && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={load}
            disabled={loading}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        )}
      </div>

      <div className="sq-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={`sq-tab ${tab === t.key ? "active" : ""}`}
            onClick={() => {
              if (t.key === "runbook") setRunbookVisited(true);
              setTab(t.key);
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* The editor stays mounted once opened, so switching tabs does not throw
          away a half-written version. Keyed by practice: a save that resolves
          after a practice switch must never reload into the other practice. */}
      {runbookVisited && (
        <div hidden={tab !== "runbook"}>
          <RunbookEditor
            key={practiceId}
            active={tab === "runbook"}
            practiceId={practiceId}
            isAdmin={isAdmin}
            isSuperAdmin={isSuperAdmin}
            readOnly={readOnly}
          />
        </div>
      )}
      {tab === "runs" && <RunsList key={practiceId} practiceId={practiceId} />}
      {tab === "skill" && (
        <SkillPage key={practiceId} practiceId={practiceId} isAdmin={isAdmin} />
      )}

      {tab === "queue" && (
        <>
          {message && (
            <div className={`sq-message ${message.type}`}>
              <span>{message.text}</span>
              <button
                className="sq-message-close"
                aria-label="Dismiss"
                onClick={() => setMessage(null)}
              >
                ×
              </button>
            </div>
          )}

          {error && (
            <div className="sq-message error" role="alert">
              <span>{error}</span>
            </div>
          )}

          <DrainHeader drain={drain} error={drainError} />

          {/* Brief 15: the portal asked for a sign-in. Retry returns leases to the
              queue, so the list reloads with it. */}
          <SignInBanner
            key={practiceId}
            practiceId={practiceId}
            signIn={signIn}
            error={signInError}
            readOnly={readOnly}
            onRetried={load}
            onOpenSession={
              isAdmin && onPageChange ? () => onPageChange("pms-coverage") : null
            }
          />

          {summary && (
            <>
              <LastRunBanner run={summary.lastRun} />

              <div className="sq-summary">
                <div className="sq-stat">
                  <span className="sq-stat-value">{summary.queued}</span>
                  <span className="sq-stat-label">Queued</span>
                </div>
                <div className="sq-stat">
                  <span className="sq-stat-value">{summary.leased}</span>
                  <span className="sq-stat-label">Being worked</span>
                </div>
                <div className={`sq-stat ${summary.held > 0 ? "red" : ""}`}>
                  <span className="sq-stat-value">{summary.held}</span>
                  <span className="sq-stat-label">Held — needs a person</span>
                </div>
                <div className="sq-stat">
                  <span className="sq-stat-value">
                    {summary.submittedToday}
                  </span>
                  <span className="sq-stat-label">Submitted today</span>
                </div>
                <div className={`sq-stat ${ageTone(summary.oldestQueuedDays)}`}>
                  <span className="sq-stat-value">
                    {summary.oldestQueuedDays == null
                      ? "—"
                      : ageLabel(summary.oldestQueuedDays)}
                  </span>
                  <span className="sq-stat-label">Oldest queued</span>
                </div>
              </div>
            </>
          )}

          {loading && !data && <p className="sq-empty">Loading the queue…</p>}

          {data && !items.some((item) => LIVE.includes(item.status)) && (
            <p className="sq-empty">
              Nothing is queued. Use “Submit to queue” on a clean claim in the
              ledger or the claim editor to add one.
            </p>
          )}

          {items.length > 0 && (
            <div className="sq-table-wrap">
              <table className="sq-table">
                <thead>
                  <tr>
                    <th>Claim</th>
                    <th>DOS</th>
                    <th>Payer</th>
                    <th className="num">Billed</th>
                    <th>Enqueued</th>
                    <th>Age</th>
                    <th>Status</th>
                    {!readOnly && <th aria-label="Actions" />}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const live = LIVE.includes(item.status);
                    const held = item.status === "held";
                    const tone = live ? ageTone(item.ageDays) : "";
                    return (
                      <tr
                        key={item.id}
                        className={`sq-row ${held ? "held" : tone}`}
                      >
                        <td className="sq-claim">#{item.claimId ?? "—"}</td>
                        <td className="sq-nowrap">
                          {item.dateOfService || "—"}
                        </td>
                        <td>{item.payerName || "—"}</td>
                        <td className="num">
                          {formatCurrency(item.billedAmount)}
                        </td>
                        <td>
                          <div>{item.enqueuedBy || "—"}</div>
                          <div className="sq-muted">
                            {item.enqueuedAt
                              ? relativeTime(item.enqueuedAt)
                              : ""}
                          </div>
                        </td>
                        <td className="sq-nowrap">
                          {live ? (
                            <span className={`sq-age-chip ${tone}`}>
                              {ageLabel(item.ageDays)}
                            </span>
                          ) : (
                            <span className="sq-muted">—</span>
                          )}
                        </td>
                        <td>
                          <span
                            className={`sq-status sq-status-${item.status}`}
                          >
                            {STATUS_LABEL[item.status] || item.status}
                          </span>
                          {item.status === "leased" && (
                            <div className="sq-muted" title="Holding the lease">
                              {item.leasedBy || "—"}
                              {leaseIsLive(item, now) ? "" : " · lease expired"}
                            </div>
                          )}
                          {held && (
                            <div className="sq-held-note">
                              Submit was pressed and no reference was captured
                              {item.leasedBy ? ` (${item.leasedBy})` : ""}. Look
                              for the claim in the portal: if it went, record it
                              with the portal’s reference.
                            </div>
                          )}
                          {item.status === "submitted" && (
                            <div className="sq-muted">
                              {item.submittedBy || "—"}
                              {item.submittedAt
                                ? ` · ${relativeTime(item.submittedAt)}`
                                : ""}
                              {item.confirmationRef
                                ? ` · ref ${item.confirmationRef}`
                                : ""}
                            </div>
                          )}
                          {item.status === "removed" && (
                            <div
                              className="sq-muted sq-removed-reason"
                              title={item.removedReason || undefined}
                            >
                              {item.removedBy || "—"}
                              {item.removedReason
                                ? ` · ${item.removedReason}`
                                : ""}
                            </div>
                          )}
                        </td>
                        {!readOnly && (
                          <td className="sq-actions">
                            {isActionable(item, now) && (
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => setSubmitting(item)}
                              >
                                I submitted
                              </button>
                            )}
                            {held && (
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setNotSent(item)}
                              >
                                Not sent
                              </button>
                            )}
                            {canRemove(item, now) && (
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setRemoving(item)}
                              >
                                Remove
                              </button>
                            )}
                            {leaseIsLive(item, now) && (
                              <button
                                className="btn btn-primary btn-sm"
                                disabled
                                title={`Leased by ${item.leasedBy || "someone"} until ${
                                  parseServerTime(
                                    item.leaseExpiresAt,
                                  )?.toLocaleTimeString([], {
                                    hour: "numeric",
                                    minute: "2-digit",
                                  }) || "the lease expires"
                                }. It unlocks when the lease expires.`}
                              >
                                I submitted
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <ReconciliationPanel
            key={practiceId}
            practiceId={practiceId}
            readOnly={readOnly}
          />
        </>
      )}

      {notSent && (
        <NotSentModal
          practiceId={practiceId}
          item={notSent}
          onClose={() => setNotSent(null)}
          onStale={load}
          onDone={onNotSent}
        />
      )}

      {submitting && (
        <SubmittedModal
          practiceId={practiceId}
          item={submitting}
          portals={portals}
          onClose={() => setSubmitting(null)}
          onStale={load}
          onSubmitted={onSubmitted}
        />
      )}

      {removing && (
        <RemoveModal
          practiceId={practiceId}
          item={removing}
          onClose={() => setRemoving(null)}
          onStale={load}
          onRemoved={onRemoved}
        />
      )}
    </div>
  );
}
